import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Crown, Rocket, Target, LineChart } from 'lucide-react';
import useScrollAnimation from '../hooks/useScrollAnimation';
import { apiFetch } from '../lib/apiClient';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Icon mapping for dynamic services
const iconMap = {
  'Crown': Crown,
  'Target': Target,
  'Rocket': Rocket,
  'LineChart': LineChart,
};

// Slug mapping for service pages
const slugMap = {
  'Strategic Advisory': 'strategic-advisory',
  'Piano Marketing Strategico': 'piano-marketing',
  'Acceleratore di Crescita': 'acceleratore-crescita',
  'Audit & Diagnosi': 'audit-diagnosi',
};

// Fallback data in case API is empty or unavailable
const fallbackServices = [
  {
    number: '01',
    icon: 'Crown',
    title: 'Strategic Advisory',
    subtitle: 'Per CEO e Founder',
    description: 'Affiancamento strategico mensile per prendere decisioni di business basate su dati e visione di lungo termine. Divento il tuo partner strategico di fiducia.',
    features: ['Call strategiche settimanali', 'Accesso diretto prioritario', 'Review trimestrale obiettivi'],
    price: 'Da €3.000/mese',
    slug: 'strategic-advisory',
  },
  {
    number: '02',
    icon: 'Target',
    title: 'Piano Marketing Strategico',
    subtitle: 'Progetto completo',
    description: 'Sviluppo di una strategia di marketing completa e personalizzata: posizionamento, messaging, canali, budget e KPI. Il blueprint per dominare il tuo mercato.',
    features: ['Analisi competitor approfondita', 'Strategia go-to-market', 'Piano operativo 12 mesi'],
    price: 'Da €8.000',
    slug: 'piano-marketing',
  },
  {
    number: '03',
    icon: 'Rocket',
    title: 'Acceleratore di Crescita',
    subtitle: 'Programma 90 giorni',
    description: 'Programma intensivo per aziende pronte a scalare. 90 giorni di lavoro fianco a fianco per implementare strategie di crescita aggressive e misurabili.',
    features: ['Workshop settimanali', 'Implementazione guidata', 'Supporto team dedicato'],
    price: 'Da €15.000',
    slug: 'acceleratore-crescita',
  },
  {
    number: '04',
    icon: 'LineChart',
    title: 'Audit & Diagnosi',
    subtitle: 'Analisi una tantum',
    description: 'Analisi completa del tuo marketing attuale: cosa funziona, cosa no, e dove stai lasciando soldi sul tavolo. Report dettagliato con priorità di intervento.',
    features: ['Audit completo canali', 'Analisi conversion funnel', 'Report con action plan'],
    price: 'Da €2.500',
    slug: 'audit-diagnosi',
  },
];

const ServicesSection = () => {
  const [services, setServices] = useState(fallbackServices);
  const [headerRef, headerVisible] = useScrollAnimation();
  const [cardsRef, cardsVisible] = useScrollAnimation();

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await apiFetch(`${API_URL}/api/services`);
        if (response.ok) {
          const data = await response.json();
          if (data.length > 0) {
            // Map API data to include number and icon
            const mappedServices = data.map((service, index) => ({
              ...service,
              number: service.number || String(index + 1).padStart(2, '0'),
              icon: service.icon || ['Crown', 'Target', 'Rocket', 'LineChart'][index % 4],
              href: '#contact',
            }));
            setServices(mappedServices);
          }
        }
      } catch (error) {
        console.error('Error fetching services:', error);
      }
    };
    fetchServices();
  }, []);

  return (
    <section id="services" className="bg-[#e2e8f0] py-16 sm:py-24 lg:py-32">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12">
        {/* Header */}
        <div 
          ref={headerRef}
          className={`max-w-3xl mb-10 sm:mb-20 animate-on-scroll ${headerVisible ? 'animated' : ''}`}
        >
          <p className="text-[#d4883a] font-semibold mb-3 sm:mb-4 tracking-wide text-sm sm:text-base">Servizi</p>
          <h2 
            className="text-3xl sm:text-4xl lg:text-6xl text-[#0b114e] leading-tight mb-4 sm:mb-6"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            Soluzioni per aziende che vogliono{' '}
            <span className="text-[#d4883a]">dominare.</span>
          </h2>
          <p className="text-gray-600 text-base sm:text-lg">
            Non offro pacchetti standardizzati. Ogni collaborazione è costruita su misura per i tuoi obiettivi specifici.
          </p>
        </div>

        {/* Services Grid */}
        <div ref={cardsRef} className="grid md:grid-cols-2 gap-4 sm:gap-8">
          {services.map((service, index) => {
            const Icon = typeof service.icon === 'string' ? (iconMap[service.icon] || Crown) : service.icon;
            return (
              <div 
                key={index} 
                className={`group relative bg-white rounded-3xl p-6 sm:p-8 lg:p-10 shadow-sm hover:shadow-xl transition-all duration-500 border border-gray-100 hover:border-[#d4883a]/30 overflow-hidden flex flex-col h-full animate-on-scroll stagger-${index + 1} ${cardsVisible ? 'animated' : ''}`}
              >
                {/* Background Number - Behind everything */}
                <span className="absolute top-2 right-4 sm:-top-2 sm:-right-2 text-7xl sm:text-8xl font-bold text-gray-100/80 group-hover:text-[#d4883a]/10 transition-colors select-none pointer-events-none" style={{ zIndex: 0 }}>
                  {service.number}
                </span>
                
                {/* Background gradient on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#d4883a]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ zIndex: 1 }} />
                
                {/* Content */}
                <div className="relative flex flex-col flex-1" style={{ zIndex: 2 }}>
                  {/* Icon + Title */}
                  <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#d4883a]/10 rounded-xl sm:rounded-2xl flex items-center justify-center group-hover:bg-[#d4883a]/20 transition-colors flex-shrink-0">
                      <Icon className="w-6 h-6 sm:w-7 sm:h-7 text-[#d4883a]" />
                    </div>
                    <div>
                      <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-[#0b114e]" style={{ fontFamily: 'Georgia, serif' }}>
                        {service.title}
                      </h3>
                      <p className="text-[#d4883a] text-xs sm:text-sm font-medium">{service.subtitle}</p>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-gray-600 text-sm sm:text-base leading-relaxed mb-4 sm:mb-6">
                    {service.description}
                  </p>

                  {/* Features */}
                  <ul className="space-y-1.5 sm:space-y-2 mb-4 sm:mb-6 flex-1">
                    {service.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-gray-600 text-xs sm:text-sm">
                        <span className="w-1.5 h-1.5 bg-[#d4883a] rounded-full flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  {/* Price + CTA - Always at bottom */}
                  <div className="flex items-center justify-between pt-4 sm:pt-6 border-t border-gray-100 mt-auto">
                    <span className="text-[#0b114e] font-bold text-base sm:text-lg">{service.price}</span>
                    <Link 
                      to={`/servizi/${service.slug || slugMap[service.title] || 'strategic-advisory'}`}
                      className="inline-flex items-center gap-1 sm:gap-2 text-[#d4883a] font-semibold text-sm sm:text-base group/link"
                    >
                      Richiedi informazioni
                      <ArrowRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom Note */}
        <div className="mt-10 sm:mt-16 text-center">
          <p className="text-gray-500 text-xs sm:text-sm">
            Requisito minimo: aziende con fatturato annuo superiore a €100.000
          </p>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
